import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Headphones, MapPin, Users, Mountain, BookOpenText, Sparkles } from "lucide-react";
import { IMAGES, TRAILS, PROCESS_STEPS, BRAND } from "../data/content";
import { WaveformDecor } from "../components/Waveform";

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${BRAND.homeHero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-brand-ink/85" />

        {/* Decorative motifs */}
        <div className="absolute inset-0 pointer-events-none">
          <WaveformDecor className="absolute bottom-32 left-0 right-0 h-20 opacity-40" color="#F7F5F0" opacity={0.4} />
          <svg className="absolute top-24 right-8 w-40 h-40 opacity-40" viewBox="0 0 200 200">
            <path d="M20 100 Q60 40 110 90 T180 60" stroke="#F7F5F0" strokeWidth="1" fill="none" strokeDasharray="2 3" />
            <circle cx="20" cy="100" r="3" fill="#C06E52" />
            <circle cx="110" cy="90" r="3" fill="#C06E52" />
            <circle cx="180" cy="60" r="3" fill="#C06E52" />
          </svg>
        </div>

        <div className="container-px relative z-10 pb-20 md:pb-28 pt-32 w-full">
          <div className="max-w-4xl animate-fade-up">
            <div className="overline text-brand-cream/90">— Place-based storytelling</div>
            <h1 className="font-serif text-brand-cream text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[1.02] mt-5">
              Every Place<br />
              <span className="italic font-light">Has a Story.</span>
            </h1>
            <div className="mt-8 max-w-2xl bg-brand-ink/45 backdrop-blur-sm border border-brand-cream/10 px-6 py-5 md:px-8 md:py-6">
              <p className="text-lg md:text-xl text-brand-cream font-light leading-relaxed">
                Walk through landscapes. Listen to local voices. Experience history,
                culture and Country where the stories belong.
              </p>
              <p className="mt-3 text-sm md:text-base text-brand-cream/85 font-light leading-relaxed">
                Talking Trails transforms landscapes, heritage sites and communities into
                living experiences through storytelling, interpretation and local voices.
              </p>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-create-trail"
                className="inline-flex items-center justify-center gap-2 bg-brand-rainforest hover:bg-brand-rainforestDark text-brand-cream px-7 py-4 text-xs uppercase tracking-[0.22em] transition-colors duration-300"
              >
                Create a Talking Trails <ArrowRight size={14} />
              </Link>
              <Link
                to="/featured-trails"
                data-testid="hero-cta-explore"
                className="inline-flex items-center justify-center gap-2 border border-brand-cream/80 text-brand-cream hover:bg-brand-cream hover:text-brand-charcoal px-7 py-4 text-xs uppercase tracking-[0.22em] transition-colors duration-300"
              >
                Explore Featured Trails
              </Link>
            </div>
          </div>

          {/* Scroll indicator / stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-brand-cream/20 pt-8">
            {[
              { k: "3", l: "Featured Trails" },
              { k: "80+", l: "Listening Stops" },
              { k: "12+", l: "Sectors Served" },
              { k: "100%", l: "Community-led" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-serif text-3xl md:text-4xl text-brand-cream">{s.k}</div>
                <div className="text-xs uppercase tracking-[0.22em] text-brand-cream/60 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION BAND — directly under hero */}
      <section className="bg-brand-rainforest text-brand-cream py-16 md:py-24">
        <div className="container-px max-w-5xl">
          <p className="font-serif text-2xl md:text-4xl lg:text-[2.75rem] leading-[1.28] tracking-tight">
            We provide world-class production and digital solutions for destinations with
            stories to share&mdash;connecting people, places, and stories.
            <span className="text-brand-cream/70"> Harness the power of Talking Trails to
            help visitors engage deeper, explore further, and discover more.</span>
          </p>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 md:py-36">
        <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <div className="overline">Introduction</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-brand-charcoal mt-4 leading-[1.05]">
              Listening Creates Connection.
            </h2>
            <div className="hairline mt-8" />
          </div>
          <div className="lg:col-span-6 lg:col-start-7 text-brand-muted text-lg leading-relaxed font-light space-y-5">
            <p>Every landscape holds stories.</p>
            <p>
              Some live in memory. Some live in language. Some remain embedded within
              rivers, mountains, coastlines, forests, streets and gathering places.
            </p>
            <p>
              Talking Trails creates opportunities for people to encounter those stories
              where they belong — in place.
            </p>
            <p>
              Rather than simply moving through a destination, visitors are invited to
              listen, reflect and develop a deeper connection with the landscape around them.
            </p>
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS - feature cards */}
      <section className="bg-brand-sand py-24 md:py-32">
        <div className="container-px">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="overline">Why Talking Trails Matters</div>
              <h2 className="font-serif text-4xl md:text-5xl text-brand-charcoal mt-4 max-w-2xl leading-tight">
                Story is how landscapes become meaningful.
              </h2>
            </div>
            <p className="text-brand-muted max-w-md font-light">
              Four ways place-based storytelling shapes the visitor experience,
              cultural connection and community memory.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-line">
            {[
              { Icon: BookOpenText, title: "Preserve Community Knowledge", desc: "Capture oral histories, local stories, cultural knowledge and lived experience before they disappear." },
              { Icon: MapPin, title: "Bring Places to Life", desc: "Allow visitors to hear stories while standing where they happened." },
              { Icon: Users, title: "Strengthen Cultural Connection", desc: "Support Traditional Owners, Elders and communities to share authentic place-based knowledge." },
              { Icon: Sparkles, title: "Create Meaningful Tourism", desc: "Offer visitors deeper engagement through storytelling, landscape immersion and local voices." },
            ].map(({ Icon, title, desc }, i) => (
              <div
                key={title}
                data-testid={`feature-card-${i + 1}`}
                className="bg-brand-cream p-8 md:p-10 flex flex-col gap-5 hover:bg-white transition-colors duration-500 group"
              >
                <div className="w-10 h-10 border border-brand-ochre/60 text-brand-ochre flex items-center justify-center">
                  <Icon size={18} />
                </div>
                <h3 className="font-serif text-2xl text-brand-charcoal leading-snug">{title}</h3>
                <p className="text-sm leading-relaxed text-brand-muted font-light">{desc}</p>
                <div className="mt-auto h-px w-8 bg-brand-ochre group-hover:w-16 transition-all duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED TRAILS PREVIEW */}
      <section className="py-24 md:py-32">
        <div className="container-px">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="overline">Featured Trails</div>
              <h2 className="font-serif text-4xl md:text-5xl mt-4 text-brand-charcoal leading-tight max-w-2xl">
                Discover places through story.
              </h2>
            </div>
            <Link
              to="/featured-trails"
              data-testid="featured-see-all"
              className="text-sm uppercase tracking-[0.22em] text-brand-ochre hover:text-brand-rainforest inline-flex items-center gap-2"
            >
              View all trails <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {Object.values(TRAILS).map((t, idx) => (
              <Link
                to={`/trails/${t.slug}`}
                key={t.slug}
                data-testid={`home-trail-card-${t.slug}`}
                className="group relative block aspect-[4/5] overflow-hidden"
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                  style={{ backgroundImage: `url(${t.hero})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                <div className="absolute top-6 left-6 flex items-center gap-2 text-brand-cream/90">
                  <span className="w-2 h-2 rounded-full bg-brand-ochre animate-pulse-dot" />
                  <span className="overline text-brand-cream/85">{String(idx + 1).padStart(2, "0")} · {t.region}</span>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-brand-cream">
                  <h3 className="font-serif text-3xl md:text-4xl leading-tight">{t.name}</h3>
                  <div className="mt-3 bg-brand-ink/55 backdrop-blur-sm border border-brand-cream/10 px-4 py-3 md:px-5 md:py-4">
                    <p className="text-sm md:text-base text-brand-cream font-light leading-relaxed">
                      {t.summary || t.intro}
                    </p>
                  </div>
                  <div className="mt-5 flex items-center gap-5 text-xs uppercase tracking-[0.22em] text-brand-cream/85">
                    <span className="flex items-center gap-1.5"><Headphones size={13} /> {t.stops} stops</span>
                    <span className="flex items-center gap-1.5"><Mountain size={13} /> {t.duration}</span>
                  </div>
                  <div className="mt-6 inline-flex items-center gap-2 text-brand-cream group-hover:text-brand-ochre transition-colors text-xs uppercase tracking-[0.22em]">
                    Explore {t.shortName || t.name.split(" ")[0]} <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW VISITORS USE IT — featuring real app artwork */}
      <section className="bg-brand-sand py-24 md:py-32">
        <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 relative">
            <div
              className="absolute inset-0 -m-8 bg-cover bg-center opacity-30 blur-2xl"
              style={{ backgroundImage: `url(${IMAGES.heroRainforest})` }}
            />
            <img
              src={BRAND.appMockup}
              alt="Kuranda Tour Stop in the Talking Trails app"
              className="relative w-full max-w-md mx-auto drop-shadow-2xl"
            />
          </div>
          <div className="lg:col-span-6">
            <div className="overline">How visitors listen</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight text-brand-charcoal">
              Download the app. Follow the trail.<br />
              <span className="italic font-light">Listen to the whispers of time.</span>
            </h2>
            <p className="mt-6 text-brand-muted text-lg font-light leading-relaxed max-w-xl">
              Every Talking Trail begins at a physical tour stop — a sign, a marker, a
              QR code in the landscape — or sent to your phone when triggered by
              geo-location or a beacon.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                { n: "01", t: "Tour stops are placed in the landscape, with on-site interpretation.", },
                { n: "02", t: "A simple QR or NFC tap launches the audio in any phone browser." },
                { n: "03", t: "Stories are recorded with community storytellers and Traditional Owners." },
                { n: "04", t: "Content evolves over time — new voices, new seasons, new stops." },
              ].map((s) => (
                <li key={s.n} className="flex items-start gap-4 text-brand-charcoal">
                  <span className="font-serif text-2xl text-brand-ochre w-10 shrink-0">{s.n}</span>
                  <span className="font-light leading-relaxed">{s.t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PROCESS PREVIEW */}
      <section className="bg-brand-ink text-brand-cream py-24 md:py-32 relative overflow-hidden">
        <WaveformDecor className="absolute top-12 left-0 right-0 h-16 opacity-30" color="#C06E52" opacity={0.5} />
        <div className="container-px relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="overline text-brand-ochre">From story to experience</div>
              <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
                A simple, careful process — shaped around people and place.
              </h2>
              <p className="mt-6 text-brand-cream/70 font-light leading-relaxed">
                Five careful steps, woven around the people, the place and the stories
                already living within a landscape.
              </p>
            </div>

            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-brand-cream/10">
              {PROCESS_STEPS.map((p) => (
                <div key={p.n} className="bg-brand-ink p-6 flex flex-col gap-3">
                  <div className="overline text-brand-ochre">{p.n}</div>
                  <h4 className="font-serif text-2xl text-brand-cream">{p.title}</h4>
                  <p className="text-xs text-brand-cream/65 font-light leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <FinalCTA />
    </div>
  );
}

export function FinalCTA() {
  return (
    <section className="relative py-32 md:py-44 overflow-hidden" data-testid="final-cta">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${IMAGES.outback})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-brand-ink/90 via-brand-ink/70 to-brand-ink/50" />
      <div className="container-px relative z-10">
        <div className="max-w-3xl text-brand-cream">
          <div className="overline text-brand-ochre">Begin a Trail</div>
          <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-4 leading-[1.05]">
            Create Talking Trails<br />
            <span className="italic font-light">for your community.</span>
          </h2>
          <p className="mt-8 text-brand-cream/80 text-lg font-light leading-relaxed max-w-2xl">
            Whether you are preserving oral history, sharing cultural knowledge, activating
            a tourism destination, interpreting a landscape or creating a new visitor
            experience — Talking Trails can help bring stories to life.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              data-testid="final-cta-start"
              className="inline-flex items-center justify-center gap-2 bg-brand-ochre text-brand-cream px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-brand-ochreDark transition-colors"
            >
              Start a Conversation <ArrowRight size={14} />
            </Link>
            <Link
              to="/contact"
              data-testid="final-cta-discovery"
              className="inline-flex items-center justify-center gap-2 border border-brand-cream/80 text-brand-cream px-8 py-4 text-xs uppercase tracking-[0.22em] hover:bg-brand-cream hover:text-brand-charcoal transition-colors"
            >
              Book a Discovery Call
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
